<template>
    <div class="lyrics-timeline-editor bg-base-100 shadow-xl rounded-xl p-6">
        <!-- 标题栏 -->
        <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold">歌词时间轴编辑器</h2>
            <div class="flex gap-2">
                <button class="btn btn-sm btn-success text-white" @click="exportLrc" :disabled="!lyrics.length">
                    <svg class="w-4 h-4 mr-1 fill-current" viewBox="0 0 24 24">
                        <path
                            d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                    保存歌词
                </button>
            </div>
        </div>

        <!-- 音频播放器区域 -->
        <div class="bg-base-200 rounded-lg p-4 mb-6">
            <div class="flex items-center gap-4 mb-3">
                <div class="text-lg font-semibold">{{ audioInfo.name || '未选择音频文件' }}</div>
                <div class="text-sm text-base-content/70" v-if="audioInfo.duration">
                    时长: {{ formatTime(audioInfo.duration) }}
                </div>
            </div>

            <!-- 音频元素 -->
            <audio ref="audioPlayer" :src="playPath" @loadedmetadata="onAudioLoaded" @timeupdate="onTimeUpdate" @ended="onAudioEnded"
                class="w-full mb-3 hidden" controls></audio>

            <!-- 播放控制 -->
            <div class="flex items-center gap-4">
                <button class="btn btn-circle btn-primary" @click="togglePlay" :disabled="!playPath">
                    <svg v-if="!isPlaying" class="w-6 h-6 fill-current" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                    <svg v-else class="w-6 h-6 fill-current" viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                </button>

                <div class="flex-1">
                    <div class="text-sm text-center mb-1">{{ formatTime(currentTime) }} / {{
                        formatTime(audioInfo.duration) }}</div>
                    <input type="range" class="range range-primary range-sm" :max="audioInfo.duration"
                        :value="currentTime" @input="seekTo" :disabled="!playPath" />
                </div>

                <div class="flex items-center gap-2">
                    <button class="btn btn-sm" @click="skipBackward" :disabled="!playPath">-5s</button>
                    <button class="btn btn-sm" @click="skipForward" :disabled="!playPath">+5s</button>
                </div>
            </div>
        </div>

        <!-- 主编辑区域 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- 歌词输入区域 -->
            <div class="bg-base-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                    <h3 class="text-lg font-semibold">歌词文本</h3>
                    <div class="flex gap-2">
                        <button class="btn btn-sm btn-outline" @click="clearLyrics">清空</button>
                    </div>
                </div>

                <textarea v-model="rawLyrics" @input="parseLyrics"
                    class="textarea textarea-bordered w-full h-80 font-mono text-sm"
                    placeholder="请输入歌词文本，每行一句歌词...&#10;支持LRC格式: [00:12.34]歌词内容"></textarea>
                <div class="mt-3 text-sm text-base-content/70">
                    <p><kbd class="kbd">Space</kbd> 播放/暂停 | <kbd class="kbd">Enter</kbd> 为当前行打轴 | <kbd class="kbd">↑↓</kbd> 切换歌词行</p>
                </div>
            </div>

            <!-- 时间轴编辑区域 -->
            <div class="bg-base-200 rounded-lg p-4">
                <div class="flex items-center justify-between mb-3">
                    <h3 class="text-lg font-semibold">时间轴编辑</h3>
                    <div class="flex gap-2">
                        <button class="btn btn-sm btn-outline" @click="autoSync"
                            :disabled="!audioInfo.duration">平均分配时间</button>
                        <button class="btn btn-sm btn-outline" @click="resetTimeline">重置时轴</button>
                    </div>
                </div>

                <div class="h-80 overflow-y-auto space-y-1">
                    <div v-for="(line, index) in lyrics" :key="index" :class="[
                        'flex items-center gap-2 p-2 rounded transition-colors cursor-pointer',
                        {
                            'bg-primary text-primary-content': currentLyricIndex === index,
                            'bg-base-300': currentLyricIndex !== index && line.time !== null,
                            'bg-warning/20': currentLyricIndex !== index && line.time === null
                        }
                    ]" @click="selectLyricLine(index)">
                        <!-- 可编辑的时间输入框 -->
                        <div class="w-20">
                            <input type="text" :value="line.time !== null ? formatTime(line.time) : ''"
                                @input="onTimeInput(index, $event)" @blur="onTimeBlur(index, $event)"
                                @keydown="onTimeKeydown(index, $event)" @click.stop :class="[
                                    'input input-xs w-full text-center font-mono transition-all duration-200 time-input',
                                    line.time !== null
                                        ? 'bg-transparent border-transparent hover:border-base-content/20 focus:border-primary'
                                        : 'bg-warning/10 border-warning/30 focus:border-warning'
                                ]" placeholder="mm:ss" maxlength="8" title="手动输入时间 (支持 mm:ss 或秒数格式)" />
                        </div>
                        <div class="flex-1 text-sm">{{ line.text }}</div>
                        <div class="flex gap-1">
                            <button class="btn btn-xs" @click.stop="setCurrentTime(index)"
                                :disabled="!audioInfo.duration" title="设置当前时间">
                                <svg class="w-3 h-3 fill-current" viewBox="0 0 24 24">
                                    <path
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                </svg>
                            </button>
                            <button class="btn btn-xs btn-error" @click.stop="clearTime(index)"
                                v-if="line.time !== null" title="清除时间">
                                <svg class="w-3 h-3 fill-current" viewBox="0 0 24 24">
                                    <path
                                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="mt-3 text-sm text-base-content/70">
                    <p class="mt-1">💡 支持手动编辑时间：mm:ss 格式 (如 1:23) 或秒数 (如 83)</p>
                    <p class="mt-1">📌 在时间框中按 <kbd class="kbd">Enter</kbd> 自动跳到下一行，<kbd class="kbd">Esc</kbd> 取消编辑</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'LyricsTimeline',
    data() {
        return {
            // 音频相关
            audioInfo: {
                name: '',
                duration: 0
            },
            isPlaying: false,
            currentTime: 0,

            // 歌词相关
            currentLyricIndex: 0,

            // 编辑状态
            isEditing: false,
            selectedLineIndex: -1
        }
    },
    props: {
        playPath: {
            type: String,
            default: ''
        },
        rawLyrics: {
            type: String,
            default: ''
        }
    },
    computed: {
        lyrics: {
            get() {
                return this.parseLyricsWithTimestamps(this.rawLyrics);
            },
            set(value) {
                return value
            }
        }
    },
    watch: {
        // 监听 playPath 的变化
        playPath: {
            handler(newPath) {
                if (newPath) {
                    this.loadAudioFromPath(newPath);
                } else {
                    this.resetAudioInfo();
                }
            },
            immediate: true
        }
    },
    mounted() {
        // 添加键盘事件监听
        document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy() {
        // 移除键盘事件监听
        document.removeEventListener('keydown', this.handleKeyDown);
    },
    methods: {
        // 键盘事件处理
        handleKeyDown(event) {
            // 如果在输入框中，不处理快捷键
            if (event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT') {
                return;
            }

            switch (event.code) {
                case 'Space':
                    event.preventDefault();
                    this.togglePlay();
                    break;
                case 'Enter':
                    event.preventDefault();
                    this.setCurrentTimeForSelected();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectPreviousLine();
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectNextLine();
                    break;
            }
        },

        // 音频相关方法
        loadAudioFromPath(path) {
            if (!path) return;
            
            // 从路径中提取文件名
            this.audioInfo.name = this.extractFileName(path);
            
            // 重置播放状态
            this.isPlaying = false;
            this.currentTime = 0;
            this.audioInfo.duration = 0;
        },

        extractFileName(path) {
            if (!path) return '未选择音频文件';
            
            // 从 URL 或路径中提取文件名
            const parts = path.split('/');
            const filename = parts[parts.length - 1];
            
            // 如果包含查询参数，移除它们
            const nameWithoutQuery = filename.split('?')[0];
            
            return decodeURIComponent(nameWithoutQuery) || '音频文件';
        },

        resetAudioInfo() {
            this.audioInfo = {
                name: '未选择音频文件',
                duration: 0
            };
            this.isPlaying = false;
            this.currentTime = 0;
        },

        onAudioLoaded() {
            this.audioInfo.duration = this.$refs.audioPlayer.duration
        },

        onTimeUpdate() {
            this.currentTime = this.$refs.audioPlayer.currentTime
            this.updateCurrentLyricIndex()
        },

        onAudioEnded() {
            this.isPlaying = false;
        },

        togglePlay() {
            if (!this.playPath) return;

            if (this.isPlaying) {
                this.$refs.audioPlayer.pause();
            } else {
                this.$refs.audioPlayer.play();
            }
            this.isPlaying = !this.isPlaying;
        },

        seekTo(event) {
            if (!this.playPath) return;
            this.$refs.audioPlayer.currentTime = parseFloat(event.target.value);
        },

        skipBackward() {
            if (!this.playPath) return;
            this.$refs.audioPlayer.currentTime = Math.max(0, this.currentTime - 5);
        },

        skipForward() {
            if (!this.playPath) return;
            this.$refs.audioPlayer.currentTime = Math.min(this.audioInfo.duration, this.currentTime + 5);
        },

        // 歌词相关方法
        parseLyrics() {
            // 触发响应式更新
            this.$forceUpdate();
        },

        parseLyricsWithTimestamps(rawText) {
            const lines = rawText.split('\n').filter(line => line.trim());
            
            return lines.map(line => {
                const trimmedLine = line.trim();
                
                // LRC格式时间戳正则表达式: [mm:ss.xx] 或 [mm:ss]
                const lrcRegex = /^\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\](.*)$/;
                const match = trimmedLine.match(lrcRegex);
                
                if (match) {
                    // 解析LRC格式时间戳
                    const minutes = parseInt(match[1], 10);
                    const seconds = parseInt(match[2], 10);
                    const milliseconds = match[3] ? parseInt(match[3].padEnd(3, '0'), 10) : 0;
                    const text = match[4].trim();
                    
                    const time = minutes * 60 + seconds + milliseconds / 1000;
                    
                    return {
                        text: text,
                        time: time
                    };
                } else {
                    // 普通歌词文本
                    return {
                        text: trimmedLine,
                        time: null
                    };
                }
            });
        },

        clearLyrics() {
            this.rawLyrics = '';
            this.lyrics = [];
            this.currentLyricIndex = 0;
        },

        // 时间轴编辑方法
        selectLyricLine(index) {
            this.currentLyricIndex = index;
            this.scrollToCurrentLyric();
            
            // 如果该行有设置时间，自动跳转到对应时间点
            const selectedLine = this.lyrics[index];
            if (selectedLine && selectedLine.time !== null && this.playPath) {
                this.$refs.audioPlayer.currentTime = selectedLine.time;
            }
        },

        setCurrentTime(index) {
            if (!this.audioInfo.duration) return;
            this.lyrics[index].time = this.currentTime;
            this.sortLyricsByTime();
        },

        setCurrentTimeForSelected() {
            if (this.currentLyricIndex >= 0 && this.audioInfo.duration) {
                this.lyrics[this.currentLyricIndex].time = this.currentTime;
                // this.sortLyricsByTime();
                // 自动移动到下一行
                if (this.currentLyricIndex < this.lyrics.length - 1) {
                    this.currentLyricIndex++;
                    this.scrollToCurrentLyric();
                }
            }
        },

        clearTime(index) {
            this.lyrics[index].time = null;
            this.$forceUpdate();
        },

        selectPreviousLine() {
            if (this.currentLyricIndex > 0) {
                this.currentLyricIndex--;
                this.scrollToCurrentLyric();
            }
        },

        selectNextLine() {
            if (this.currentLyricIndex < this.lyrics.length - 1) {
                this.currentLyricIndex++;
                this.scrollToCurrentLyric();
            }
        },

        sortLyricsByTime() {
            // 保持时间为 null 的歌词在原位置，只对有时间的歌词排序
            const withTime = this.lyrics.filter(line => line.time !== null).sort((a, b) => a.time - b.time);
            const withoutTime = this.lyrics.filter(line => line.time === null);

            // 重新组合，保持原有顺序
            this.lyrics = this.lyrics.map(line => {
                if (line.time !== null) {
                    return withTime.shift();
                }
                return line;
            });
        },

        updateCurrentLyricIndex() {
            let newIndex = this.currentLyricIndex;

            for (let i = 0; i < this.lyrics.length; i++) {
                const line = this.lyrics[i];
                const nextLine = this.lyrics[i + 1];

                if (line.time !== null &&
                    this.currentTime >= line.time &&
                    (!nextLine || nextLine.time === null || this.currentTime < nextLine.time)) {
                    newIndex = i + 1;
                    break;
                }
            }

            // 如果索引发生变化，更新并滚动到新位置
            if (newIndex !== this.currentLyricIndex) {
                this.currentLyricIndex = newIndex;
                this.scrollToCurrentLyric();
            }
        },

        // 滚动到当前歌词行
        scrollToCurrentLyric() {
            this.$nextTick(() => {
                const lyricsContainer = this.$el.querySelector('.overflow-y-auto');
                const currentLyricElement = lyricsContainer?.children[this.currentLyricIndex];

                if (currentLyricElement && lyricsContainer) {
                    const containerRect = lyricsContainer.getBoundingClientRect();
                    const elementRect = currentLyricElement.getBoundingClientRect();

                    // 检查当前歌词是否在可视区域内
                    const isVisible = elementRect.top >= containerRect.top &&
                        elementRect.bottom <= containerRect.bottom;

                    if (!isVisible) {
                        // 滚动到容器中心位置
                        const scrollTop = currentLyricElement.offsetTop -
                            lyricsContainer.offsetTop -
                            (lyricsContainer.clientHeight / 2) +
                            (currentLyricElement.clientHeight / 2);

                        lyricsContainer.scrollTo({
                            top: Math.max(0, scrollTop),
                            behavior: 'smooth'
                        });
                    }
                }
            });
        },

        autoSync() {
            // 简单的自动同步：平均分配时间
            const hasTime = this.lyrics.filter(line => line.time !== null);
            if (hasTime.length < 2) return;

            const totalDuration = this.audioInfo.duration;
            const interval = totalDuration / this.lyrics.length;

            this.lyrics.forEach((line, index) => {
                if (line.time === null) {
                    line.time = index * interval;
                }
            });

            this.sortLyricsByTime();
        },

        resetTimeline() {
            this.lyrics.forEach(line => {
                line.time = null;
            });
        },

        // 导出功能
        exportLrc() {
            // 分离有时间轴和无时间轴的歌词
            const timedLyrics = this.lyrics.filter(line => line.time !== null);
            const untimedLyrics = this.lyrics.filter(line => line.time === null);
            
            // 处理有时间轴的歌词
            const timedContent = timedLyrics
                .sort((a, b) => a.time - b.time)
                .map(line => {
                    const minutes = Math.floor(line.time / 60);
                    const seconds = Math.floor(line.time % 60);
                    const milliseconds = Math.floor((line.time % 1) * 100);
                    return `[${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(2, '0')}]${line.text}`;
                })
                .join('\n');
            
            // 处理无时间轴的歌词
            const untimedContent = untimedLyrics
                .map(line => line.text)
                .join('\n');
            
            // 合并内容，有时间轴的歌词在前，无时间轴的在后
            let lrcContent = '';
            if (timedContent) {
                lrcContent += timedContent;
            }
            if (untimedContent) {
                if (lrcContent) lrcContent += '\n';
                lrcContent += untimedContent;
            }
            
            this.$emit('saveLyrics', lrcContent)
        },

        // 工具方法
        formatTime(seconds) {
            if (!seconds && seconds !== 0) return '00:00';
            const minutes = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        },

        // 时间输入相关方法
        onTimeInput(index, event) {
            // 实时验证和更新时间，但不强制格式
            const value = event.target.value.trim();
            if (value === '') {
                this.lyrics[index].time = null;
                return;
            }

            // 尝试解析时间格式
            this.parseAndSetTime(index, value);
        },

        onTimeBlur(index, event) {
            // 失焦时进行最终验证和格式化
            const value = event.target.value.trim();
            if (value === '') {
                this.lyrics[index].time = null;
                return;
            }

            this.parseAndSetTime(index, value);

            // 重新格式化显示
            this.$nextTick(() => {
                if (this.lyrics[index].time !== null) {
                    event.target.value = this.formatTime(this.lyrics[index].time);
                }
            });
        },

        onTimeKeydown(index, event) {
            // 处理特殊按键
            if (event.key === 'Enter') {
                event.preventDefault();
                event.target.blur(); // 触发 blur 事件进行验证

                // 自动移动到下一行
                if (index < this.lyrics.length - 1) {
                    this.currentLyricIndex = index + 1;
                    this.scrollToCurrentLyric();
                    // 聚焦到下一个时间输入框
                    this.$nextTick(() => {
                        const nextInput = this.$el.querySelectorAll('input[type="text"]')[index + 1];
                        if (nextInput) {
                            nextInput.focus();
                        }
                    });
                }
            } else if (event.key === 'Escape') {
                event.preventDefault();
                event.target.blur();
            }
        },

        parseAndSetTime(index, timeString) {
            // 解析时间字符串并设置时间
            let time = null;

            // 支持多种格式：mm:ss, m:ss, ss
            const timeRegex = /^(\d{1,2}):(\d{2})$/;
            const secondsRegex = /^(\d+)$/;

            if (timeRegex.test(timeString)) {
                // mm:ss 格式
                const match = timeString.match(timeRegex);
                const minutes = parseInt(match[1], 10);
                const seconds = parseInt(match[2], 10);

                if (minutes >= 0 && seconds >= 0 && seconds < 60) {
                    time = minutes * 60 + seconds;
                }
            } else if (secondsRegex.test(timeString)) {
                // 纯秒数格式
                const totalSeconds = parseInt(timeString, 10);
                if (totalSeconds >= 0) {
                    time = totalSeconds;
                }
            }

            this.lyrics[index].time = time;
        }
    }
}
</script>

<style scoped>
.lyrics-timeline-editor {
    max-width: 1200px;
    margin: 0 auto;
}

.edit-item {
    @apply flex items-center mb-4;
}

.label1 {
    @apply text-sm font-medium mr-2 min-w-16;
}

.can-copy {
    @apply select-text;
}

/* 时间输入框样式 */
.time-input {
    color: hsl(var(--bc)) !important;
}

.time-input:focus {
    color: hsl(var(--bc)) !important;
    background-color: hsl(var(--b1)) !important;
}

.time-input::placeholder {
    color: hsl(var(--bc) / 0.5) !important;
}

/* 自定义滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    @apply bg-base-300 rounded;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    @apply bg-base-content/20 rounded;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-base-content/30;
}
</style>